import type { ShipSpec } from './ship.js';
import type { Vec3 } from './vec3.js';

export type HardpointId = string;
export type ModuleId = string;
export type HardpointSize = 'small' | 'medium' | 'large';
export type HardpointKind = 'weapon' | 'component';
export type MountType = 'fixed' | 'gimbal' | 'turret' | 'internal';
export type PaletteRole = 'attack' | 'defense' | 'mobility' | 'systems';

export interface MountTransform {
  position: Vec3;
  forward: Vec3;
  up?: Vec3;
}

export interface Hardpoint {
  id: HardpointId;
  nodeName: string;
  kind: HardpointKind;
  size: HardpointSize;
  mountType: MountType;
  paletteRole: PaletteRole;
  powerBudget: number;
  transform: MountTransform;
}

export const SHIP_NUMERIC_STATS = [
  'thrust',
  'linearDamping',
  'maxSpeedKmS',
  'mouseSens',
  'keyTurnRate',
  'rollRate',
  'angularDamping',
  'pitchYawAccel',
  'rollAccel',
  'maxAngAccel',
  'optimalSpeedLo',
  'optimalSpeedHi',
  'fuelMax',
  'hullMax',
  'shieldMax',
  'energyMax',
  'fuelBurnPerSec',
  'energyRegenPerSec',
  'shieldRegenPerSec',
] as const;

export type ShipNumericStat = (typeof SHIP_NUMERIC_STATS)[number];
export type ComponentAddKey = `${ShipNumericStat}Add`;
export type ComponentMulKey = `${ShipNumericStat}Mul`;
export type ComponentModifierKey = ComponentAddKey | ComponentMulKey;
export type ComponentModifiers = Partial<Record<ComponentModifierKey, number>>;

interface BaseModule {
  id: ModuleId;
  kind: HardpointKind;
  size: HardpointSize;
  name: string;
  paletteRole: PaletteRole;
  massKg: number;
  powerDraw: number;
}

export interface WeaponModule extends BaseModule {
  kind: 'weapon';
  damage: number;
  projectileSpeedKmS: number;
  cooldownSec: number;
  energyCost: number;
  rangeKm: number;
}

export interface ComponentModule extends BaseModule {
  kind: 'component';
  modifiers: ComponentModifiers;
}

export type Module = WeaponModule | ComponentModule;
export type ModuleCatalog = Readonly<Record<ModuleId, Module>>;

export interface Loadout {
  hardpoints: Hardpoint[];
  fitted: Partial<Record<HardpointId, ModuleId>>;
}

export type StatTotals = Record<ShipNumericStat, number>;

export interface LoadoutTotals {
  moduleMassKg: number;
  powerDraw: number;
  weaponCount: number;
  componentCount: number;
  modifierAdds: StatTotals;
  modifierMuls: StatTotals;
}

export interface WeaponProfile {
  hardpointId: HardpointId;
  moduleId: ModuleId;
  name: string;
  size: HardpointSize;
  mountType: MountType;
  paletteRole: PaletteRole;
  transform: MountTransform;
  damage: number;
  projectileSpeedKmS: number;
  cooldownSec: number;
  energyCost: number;
  rangeKm: number;
  powerDraw: number;
  overPower: boolean;
}

export interface EffectiveShip {
  baseSpec: ShipSpec;
  spec: ShipSpec;
  loadout: Loadout;
  totals: LoadoutTotals;
  weapons: WeaponProfile[];
  overPower: boolean;
}

export const STARTER_WEAPON_MODULES = {
  'pulse-laser-s1': {
    id: 'pulse-laser-s1',
    kind: 'weapon',
    size: 'small',
    name: 'Pulse Laser S1',
    paletteRole: 'attack',
    massKg: 250,
    powerDraw: 4,
    damage: 12,
    projectileSpeedKmS: 40,
    cooldownSec: 0.25,
    energyCost: 8,
    rangeKm: 120,
  },
  'autocannon-m2': {
    id: 'autocannon-m2',
    kind: 'weapon',
    size: 'medium',
    name: 'Autocannon M2',
    paletteRole: 'attack',
    massKg: 600,
    powerDraw: 6,
    damage: 20,
    projectileSpeedKmS: 28,
    cooldownSec: 0.45,
    energyCost: 6,
    rangeKm: 90,
  },
  'railgun-m2': {
    id: 'railgun-m2',
    kind: 'weapon',
    size: 'medium',
    name: 'Railgun M2',
    paletteRole: 'attack',
    massKg: 850,
    powerDraw: 9,
    damage: 32,
    projectileSpeedKmS: 60,
    cooldownSec: 1.2,
    energyCost: 18,
    rangeKm: 180,
  },
} as const satisfies Record<string, WeaponModule>;

export const STARTER_COMPONENT_MODULES = {
  'shield-booster-s1': {
    id: 'shield-booster-s1',
    kind: 'component',
    size: 'small',
    name: 'Shield Booster S1',
    paletteRole: 'defense',
    massKg: 200,
    powerDraw: 3,
    modifiers: {
      shieldMaxAdd: 25,
      shieldRegenPerSecAdd: 1,
    },
  },
  'reactor-overclock-m2': {
    id: 'reactor-overclock-m2',
    kind: 'component',
    size: 'medium',
    name: 'Reactor Overclock M2',
    paletteRole: 'systems',
    massKg: 350,
    powerDraw: 5,
    modifiers: {
      thrustMul: 1.05,
      energyMaxAdd: 20,
      energyRegenPerSecMul: 1.1,
    },
  },
  'thruster-tuning-m2': {
    id: 'thruster-tuning-m2',
    kind: 'component',
    size: 'medium',
    name: 'Thruster Tuning M2',
    paletteRole: 'mobility',
    massKg: 300,
    powerDraw: 4,
    modifiers: {
      thrustMul: 1.1,
      maxSpeedKmSMul: 1.08,
      fuelBurnPerSecMul: 1.05,
    },
  },
  'armor-plating-l3': {
    id: 'armor-plating-l3',
    kind: 'component',
    size: 'large',
    name: 'Armor Plating L3',
    paletteRole: 'defense',
    massKg: 2_000,
    powerDraw: 2,
    modifiers: {
      hullMaxAdd: 60,
      linearDampingAdd: 0.03,
    },
  },
} as const satisfies Record<string, ComponentModule>;

export const STARTER_MODULES = {
  ...STARTER_WEAPON_MODULES,
  ...STARTER_COMPONENT_MODULES,
} as const satisfies Record<string, Module>;
